/**
 * Hook useDeliveryDecisionAlert (#231)
 *
 * Entrega por aplicativo: quando nenhuma empresa aceita depois das rodadas, o
 * frete passa do limite ou a empresa cancela depois da coleta, a loja precisa
 * decidir. Toca um alerta curto e mostra um aviso fixo com o link do pedido.
 *
 * @author Equipe VendeAi
 * @since 2.7.0
 */

import { useCallback } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { useSocketEvent } from './useWebSocket';

interface DispatchEvent {
  data?: { orderId?: string; orderNumber?: number | null; alert?: boolean; message?: string };
}

/** Dois bipes curtos (Web Audio; silencioso se o navegador bloquear o som) */
function playAlert(): void {
  try {
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    [0, 0.3].forEach(offset => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'square';
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + offset);
      gain.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + offset + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + offset + 0.2);
      osc.connect(gain).connect(ctx.destination);
      osc.start(ctx.currentTime + offset);
      osc.stop(ctx.currentTime + offset + 0.22);
    });
    setTimeout(() => void ctx.close().catch(() => undefined), 1000);
  } catch {
    // Sem áudio: o aviso na tela continua
  }
}

export function useDeliveryDecisionAlert(enabled: boolean): void {
  const handle = useCallback((event: DispatchEvent) => {
    const data = event?.data;
    if (!enabled || !data?.alert || !data.orderId) return;
    playAlert();
    toast.error(
      t => (
        <span className="text-sm">
          <strong>Pedido #{data.orderNumber ?? ''}: entrega por aplicativo.</strong> {data.message}.{' '}
          <Link to={`/pedidos/${data.orderId}`} onClick={() => toast.dismiss(t.id)} className="font-medium underline">
            Abrir o pedido
          </Link>
        </span>
      ),
      { id: `delivery-decision-${data.orderId}`, duration: Infinity },
    );
  }, [enabled]);

  useSocketEvent<DispatchEvent>('delivery:dispatch', handle);
}

export default useDeliveryDecisionAlert;
